import { SentenceAnnotation } from '../../types/chat';

export interface HighlightSegment {
  text: string;
  /** Empty for the stretches between annotations. */
  status: '' | 'supported' | 'unsupported' | string;
}

/** Drops markdown bold and collapses runs of whitespace to one space. */
const normalize = (value: string) => value.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();

/**
 * The span of `haystack` that matches `needle` once both are normalised, as
 * offsets into the original haystack. The verifier annotates the plain text it
 * was given, but the message still carries its markdown, so an exact indexOf
 * misses any sentence containing bold or a wrapped line.
 */
export const fuzzyFind = (
  haystack: string,
  needle: string,
  startFrom: number = 0,
): [number, number] | null => {
  const normNeedle = normalize(needle);
  if (!normNeedle) return null;

  let normIdx = 0;
  let matchStart = -1;

  for (let i = startFrom; i < haystack.length; i++) {
    const ch = haystack[i];
    if (ch === '*' && i + 1 < haystack.length && haystack[i + 1] === '*') {
      i++;
      continue;
    }
    const normCh = /\s/.test(ch) ? ' ' : ch;
    if (normCh === ' ' && normIdx > 0 && normNeedle[normIdx - 1] === ' ') {
      continue;
    }

    if (normCh === normNeedle[normIdx]) {
      if (normIdx === 0) matchStart = i;
      normIdx++;
      if (normIdx === normNeedle.length) {
        return [matchStart, i + 1];
      }
    } else if (matchStart >= 0) {
      // Rewind to just after where this attempt started, so a partial match
      // does not swallow the real one — needle "aab" inside "aaab".
      i = matchStart;
      matchStart = -1;
      normIdx = 0;
    }
  }

  return null;
};

/**
 * Splits `content` into consecutive segments, each either plain or carrying an
 * annotation's status. Search advances past each match so a sentence repeated
 * later in the answer cannot be claimed twice, and an annotation that no longer
 * matches is skipped rather than shifting the ones after it.
 */
export function buildHighlightSegments(
  content: string,
  annotations: SentenceAnnotation[] = [],
): HighlightSegment[] {
  const segments: HighlightSegment[] = [];
  let pos = 0;

  for (const annotation of annotations) {
    if (annotation.status === 'neutral') continue;

    const range = fuzzyFind(content, annotation.text, pos);
    if (!range) continue;

    if (range[0] > pos) {
      segments.push({ text: content.slice(pos, range[0]), status: '' });
    }
    segments.push({ text: content.slice(range[0], range[1]), status: annotation.status });
    pos = range[1];
  }

  if (pos < content.length) {
    segments.push({ text: content.slice(pos), status: '' });
  }

  return segments;
}
